import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import {
  DEFAULT_THEME_COLORS,
  ThemeColorsSchema,
  normalizeThemeColors,
  type ThemeColors,
} from "@/lib/theme-settings";
import {
  DEFAULT_HOME_THEME_DETAILS,
  HomeThemeDetailsSchema,
  normalizeHomeThemeDetails,
  type HomeThemeDetails,
} from "@/lib/home-theme-details";

export type StoredThemeColors = ThemeColors & { home_details: HomeThemeDetails };

function normalizeStoredThemeColors(input: unknown): StoredThemeColors {
  const raw = input && typeof input === "object" ? input as Record<string, unknown> : {};
  return {
    ...normalizeThemeColors(raw),
    home_details: normalizeHomeThemeDetails(raw.home_details),
  };
}

const DEFAULTS = {
  home_projects_limit: 6,
  work_projects_limit: 0,
  theme_colors: {
    ...DEFAULT_THEME_COLORS,
    home_details: DEFAULT_HOME_THEME_DETAILS,
  } as StoredThemeColors,
  theme_storage_ready: false,
};

function publicSupabase() {
  const url = process.env.SUPABASE_URL || process.env.VITE_SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY || process.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) {
    throw new Error("Missing SUPABASE_URL / SUPABASE_PUBLISHABLE_KEY");
  }
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

export type SiteSettings = {
  home_projects_limit: number;
  work_projects_limit: number;
  theme_colors: StoredThemeColors;
  theme_storage_ready: boolean;
};

export async function loadPublicSiteSettings(): Promise<SiteSettings> {
  try {
    const client = publicSupabase();
    const { data, error } = await (client as any)
      .from("site_settings")
      .select("home_projects_limit, work_projects_limit, theme_colors")
      .eq("id", "global")
      .maybeSingle();

    if (error) {
      const legacy = await client
        .from("site_settings")
        .select("home_projects_limit, work_projects_limit")
        .eq("id", "global")
        .maybeSingle();
      if (legacy.error) {
        console.error("loadPublicSiteSettings error", error, legacy.error);
        return DEFAULTS;
      }
      return {
        home_projects_limit: Number(legacy.data?.home_projects_limit ?? DEFAULTS.home_projects_limit),
        work_projects_limit: Number(legacy.data?.work_projects_limit ?? DEFAULTS.work_projects_limit),
        theme_colors: DEFAULTS.theme_colors,
        theme_storage_ready: false,
      };
    }

    return {
      home_projects_limit: Number(data?.home_projects_limit ?? DEFAULTS.home_projects_limit),
      work_projects_limit: Number(data?.work_projects_limit ?? DEFAULTS.work_projects_limit),
      theme_colors: normalizeStoredThemeColors(data?.theme_colors),
      theme_storage_ready: true,
    };
  } catch (err) {
    console.error("loadPublicSiteSettings exception", err);
    return DEFAULTS;
  }
}

export const getSiteSettings = createServerFn({ method: "GET" }).handler(async () => ({
  settings: await loadPublicSiteSettings(),
}));

const UpdateSchema = z.object({
  home_projects_limit: z.number().int().min(0).max(50),
  work_projects_limit: z.number().int().min(0).max(50),
});

async function requireAdmin(context: { supabase: any; userId: string }) {
  const { data: roleRow } = await context.supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", context.userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!roleRow) throw new Error("Forbidden");
}

async function readStoredThemeColors(): Promise<Record<string, unknown>> {
  const { data } = await (supabaseAdmin as any)
    .from("site_settings")
    .select("theme_colors")
    .eq("id", "global")
    .maybeSingle();
  return data?.theme_colors && typeof data.theme_colors === "object"
    ? data.theme_colors as Record<string, unknown>
    : {};
}

async function writeStoredThemeColors(themeColors: Record<string, unknown>) {
  const { error } = await (supabaseAdmin as any)
    .from("site_settings")
    .upsert({
      id: "global",
      theme_colors: themeColors,
      updated_at: new Date().toISOString(),
    });
  if (error) {
    const message = String(error.message || "");
    if (message.includes("theme_colors") || message.includes("schema cache")) {
      throw new Error(
        "Theme storage is not installed yet. Run supabase/migrations/20260729171000_dynamic_theme_colors.sql in the Supabase SQL Editor, then reload this page.",
      );
    }
    throw new Error(message || "Failed to publish theme settings");
  }
}

export const updateSiteSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => UpdateSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const { error } = await supabaseAdmin
      .from("site_settings")
      .upsert({
        id: "global",
        home_projects_limit: data.home_projects_limit,
        work_projects_limit: data.work_projects_limit,
      });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const updateThemeColors = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ThemeColorsSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const existing = await readStoredThemeColors();
    const homeDetails = normalizeHomeThemeDetails(existing.home_details);
    await writeStoredThemeColors({ ...data, home_details: homeDetails });
    return { ok: true, theme_colors: data };
  });

export const updateHomeThemeDetails = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => HomeThemeDetailsSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const existing = await readStoredThemeColors();
    const baseTheme = normalizeThemeColors(existing);
    await writeStoredThemeColors({ ...baseTheme, home_details: data });
    return { ok: true, home_details: data };
  });
